import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { adminCompetitions, adminCreateComp, adminUpdateComp, adminUploadImage } from '@/lib/api';

const empty = {
  title: '', subtitle: '', slug: '', category: 'Padel Holidays', prize_description: '', cash_alternative_gbp: 0,
  ticket_price_gbp: 2.99, total_tickets: 1000, free_entry_cap: '', draw_date: '', hero_image_url: '',
  gallery: [], background_images: [], skill_question: '', skill_options: 'Option A\nOption B\nOption C\nOption D', skill_correct_index: 0,
  featured: false, instant_win: false, location: '', details_bullets: '', status: 'COMING_SOON', go_live_at: '',
};

const Field = ({ label, hint, children, w = 'full' }) => (
  <label className={`block ${w === 'half' ? 'md:col-span-1' : 'md:col-span-2'}`}>
    <div className="font-mono text-[10px] uppercase text-muted-sage mb-1">{label}</div>
    {children}
    {hint ? <div className="mt-1 text-[11px] text-muted-sage">{hint}</div> : null}
  </label>
);

const input = 'w-full h-11 rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 text-sm focus:border-[color:var(--pd-accent-yellow)]';
const textarea = 'w-full rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 py-2 text-sm focus:border-[color:var(--pd-accent-yellow)]';

// Resolve upload URLs (which come back as "/api/uploads/xxx.jpg") against the backend host.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const resolveImg = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('/api/uploads/')) return `${BACKEND_URL}${u}`;
  return u;
};

const UploadTile = ({ url, onReplace, onRemove, uploading, label = 'Upload image' }) => (
  <div className="relative rounded-xl overflow-hidden border border-pd bg-[color:var(--pd-bg)] aspect-[16/10] group">
    {url ? (
      <>
        <img src={resolveImg(url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <label className="cursor-pointer bg-white/90 text-[#14251c] text-xs font-medium px-3 py-1.5 rounded-lg">
            {uploading ? 'Uploading…' : 'Replace'}
            <input type="file" accept="image/*" className="hidden" onChange={onReplace} disabled={uploading} />
          </label>
          {onRemove ? (
            <button type="button" onClick={onRemove} className="bg-white/90 text-[color:var(--pd-danger)] text-xs font-medium px-3 py-1.5 rounded-lg">Remove</button>
          ) : null}
        </div>
      </>
    ) : (
      <label className="absolute inset-0 flex flex-col items-center justify-center gap-1 cursor-pointer text-center px-4">
        <div className="font-mono text-[10px] uppercase text-muted-sage">{uploading ? 'Uploading…' : label}</div>
        <div className="text-[11px] text-muted-sage">JPEG · PNG · WEBP (auto-optimised)</div>
        <input type="file" accept="image/*" className="hidden" onChange={onReplace} disabled={uploading} />
      </label>
    )}
  </div>
);

const AdminCompetitionEditor = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [heroUploading, setHeroUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [backgroundUploading, setBackgroundUploading] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    if (!id) return;
    adminCompetitions().then((d) => {
      const c = (d.items || []).find((x) => x.id === id);
      if (!c) return;
      setForm({
        ...c,
        gallery: Array.isArray(c.gallery) ? c.gallery : [],
        background_images: Array.isArray(c.background_images) ? c.background_images : [],
        skill_options: (c.skill_options || []).join('\n'),
        details_bullets: (c.details_bullets || []).join('\n'),
        cash_alternative_gbp: c.cash_alternative_gbp || 0,
        status: c.status || 'COMING_SOON',
        go_live_at: c.go_live_at || '',
      });
    });
  }, [id]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadHero = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHeroUploading(true);
    try {
      const res = await adminUploadImage(file);
      update('hero_image_url', res.url);
      toast.success('Hero image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setHeroUploading(false);
      e.target.value = '';
    }
  };

  const uploadGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setGalleryUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const res = await adminUploadImage(file);
        urls.push(res.url);
      }
      setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...urls] }));
      toast.success(`${urls.length} image${urls.length === 1 ? '' : 's'} added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setGalleryUploading(false);
      e.target.value = '';
    }
  };

  // Background images — separate from gallery. Populates the sticky
  // scrolling backdrop on CompetitionPage. Up to 3 are used in order.
  // If left empty, CompetitionPage falls back to `gallery` then `hero_image_url`.
  const uploadBackgrounds = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBackgroundUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const res = await adminUploadImage(file);
        urls.push(res.url);
      }
      setForm((f) => ({ ...f, background_images: [...(f.background_images || []), ...urls] }));
      toast.success(`${urls.length} background${urls.length === 1 ? '' : 's'} added`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setBackgroundUploading(false);
      e.target.value = '';
    }
  };

  const removeBackgroundAt = (idx) => {
    setForm((f) => ({ ...f, background_images: (f.background_images || []).filter((_, i) => i !== idx) }));
  };

  const replaceGalleryAt = (idx) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGalleryUploading(true);
    try {
      const res = await adminUploadImage(file);
      setForm((f) => {
        const g = [...(f.gallery || [])];
        g[idx] = res.url;
        return { ...f, gallery: g };
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setGalleryUploading(false);
      e.target.value = '';
    }
  };

  const removeGalleryAt = (idx) => {
    setForm((f) => {
      const g = [...(f.gallery || [])];
      g.splice(idx, 1);
      return { ...f, gallery: g };
    });
  };

  const moveGallery = (idx, dir) => {
    setForm((f) => {
      const g = [...(f.gallery || [])];
      const j = idx + dir;
      if (j < 0 || j >= g.length) return f;
      [g[idx], g[j]] = [g[j], g[idx]];
      return { ...f, gallery: g };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.hero_image_url) {
      toast.error('Please upload a hero image before saving.');
      return;
    }
    setLoading(true);
    const payload = {
      ...form,
      cash_alternative_gbp: Number(form.cash_alternative_gbp) || 0,
      ticket_price_gbp: Number(form.ticket_price_gbp),
      total_tickets: Number(form.total_tickets),
      free_entry_cap: form.free_entry_cap === '' || form.free_entry_cap === null ? null : Number(form.free_entry_cap),
      skill_correct_index: Number(form.skill_correct_index) || 0,
      gallery: (form.gallery || []).filter(Boolean),
      background_images: (form.background_images || []).filter(Boolean),
      skill_options: (form.skill_options || '').split('\n').map((s) => s.trim()).filter(Boolean),
      details_bullets: (form.details_bullets || '').split('\n').map((s) => s.trim()).filter(Boolean),
      // Only send go_live_at when we're COMING_SOON — if the admin flips to
      // LIVE the schedule is meaningless (manual toggle = final word).
      go_live_at: form.status === 'COMING_SOON' && form.go_live_at
        ? new Date(form.go_live_at).toISOString()
        : null,
    };
    try {
      if (id) await adminUpdateComp(id, payload);
      else await adminCreateComp(payload);
      toast.success('Saved');
      setPreviewKey((k) => k + 1); // refresh preview iframe
      if (!id) nav('/admin/competitions');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setLoading(false); }
  };

  const previewSrc = useMemo(() => {
    if (!form.slug) return '';
    return `/competitions/${form.slug}`;
  }, [form.slug]);

  return (
    <div data-testid="admin-competition-editor">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl">{id ? 'Edit competition' : 'New competition'}</h1>
          <p className="text-sm text-muted-sage mt-1">
            Images upload directly to the app server. All changes go live once you press Save.
          </p>
        </div>
        {id && form.slug ? (
          <a href={previewSrc} target="_blank" rel="noreferrer" className="text-xs font-mono uppercase text-muted-sage hover:text-[color:var(--pd-text)] border border-pd rounded-lg px-3 py-2">
            Open live page ↗
          </a>
        ) : null}
      </div>

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,540px)] gap-6">
        {/* FORM */}
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Hero + gallery */}
          <div className="md:col-span-2 rounded-2xl border border-pd bg-[color:var(--pd-surface)] p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-display text-xl">Hero image</div>
                <div className="text-[11px] text-muted-sage">Landscape photos work best. We auto-resize to 2000px and optimise.</div>
              </div>
            </div>
            <div className="max-w-md">
              <UploadTile
                url={form.hero_image_url}
                uploading={heroUploading}
                onReplace={uploadHero}
                onRemove={form.hero_image_url ? () => update('hero_image_url', '') : null}
                label="Upload hero image"
              />
            </div>
          </div>

          <div className="md:col-span-2 rounded-2xl border border-pd bg-[color:var(--pd-surface)] p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-display text-xl">Gallery</div>
                <div className="text-[11px] text-muted-sage">Up to 8 images. Reorder with the arrows; the first image shows first on the competition page.</div>
              </div>
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                disabled={galleryUploading}
                className="text-xs border border-pd rounded-lg px-3 py-1.5 hover:border-[color:var(--pd-accent-yellow)] disabled:opacity-50"
                data-testid="admin-gallery-add"
              >
                {galleryUploading ? 'Uploading…' : '+ Add images'}
              </button>
              <input ref={galleryInputRef} type="file" accept="image/*" multiple className="hidden" onChange={uploadGallery} />
            </div>
            {(form.gallery || []).length === 0 ? (
              <div className="text-sm text-muted-sage italic">No gallery images yet.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(form.gallery || []).map((url, i) => (
                  <div key={`${url}-${i}`} className="relative group rounded-xl overflow-hidden border border-pd aspect-[16/10]">
                    <img src={resolveImg(url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors" />
                    <div className="absolute inset-x-2 bottom-2 flex items-center justify-between gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex gap-1">
                        <button type="button" onClick={() => moveGallery(i, -1)} disabled={i === 0} className="bg-white/90 text-[#14251c] text-xs px-2 py-1 rounded disabled:opacity-40">←</button>
                        <button type="button" onClick={() => moveGallery(i, 1)} disabled={i === (form.gallery.length - 1)} className="bg-white/90 text-[#14251c] text-xs px-2 py-1 rounded disabled:opacity-40">→</button>
                      </div>
                      <div className="flex gap-1">
                        <label className="bg-white/90 text-[#14251c] text-xs px-2 py-1 rounded cursor-pointer">
                          Replace
                          <input type="file" accept="image/*" className="hidden" onChange={replaceGalleryAt(i)} />
                        </label>
                        <button type="button" onClick={() => removeGalleryAt(i)} className="bg-white/90 text-[color:var(--pd-danger)] text-xs px-2 py-1 rounded">Remove</button>
                      </div>
                    </div>
                    {i === 0 ? (
                      <div className="absolute top-2 left-2 bg-[color:var(--pd-accent-yellow)] text-[#14251c] text-[10px] font-mono uppercase px-2 py-0.5 rounded">First</div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* BACKGROUND IMAGES — dedicated moody backdrops for the sticky
              scroll effect on the public competition page. Up to 3 used in
              order (top / middle / bottom of the page). Kept separate from
              the in-page gallery carousel so admins can pick photos that
              work as backdrops without them cluttering the product gallery. */}
          <div className="md:col-span-2 rounded-2xl border border-pd bg-[color:var(--pd-surface)] p-4" data-testid="admin-background-images-section">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-display text-xl">Background images</div>
                <div className="text-[11px] text-muted-sage">Up to 3 images used as sticky scroll backdrops on the competition page (top / middle / bottom). If left empty, the gallery images above are used instead. Uploads are automatically darkened at render time — you do not need to pre-edit them.</div>
              </div>
              <label className="text-xs border border-pd rounded-lg px-3 py-1.5 hover:border-[color:var(--pd-accent-yellow)] cursor-pointer" data-testid="admin-background-add">
                {backgroundUploading ? 'Uploading\u2026' : '+ Add backgrounds'}
                <input type="file" accept="image/*" multiple className="hidden" onChange={uploadBackgrounds} disabled={backgroundUploading} />
              </label>
            </div>
            {(form.background_images || []).length === 0 ? (
              <div className="text-sm text-muted-sage italic">No dedicated background images yet &mdash; the page will fall back to the gallery photos above.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(form.background_images || []).map((url, i) => (
                  <div key={`${url}-${i}`} className="relative group rounded-xl overflow-hidden border border-pd aspect-[16/10]" data-testid={`admin-background-tile-${i}`}>
                    <img src={resolveImg(url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/60 pointer-events-none" title="Preview of darkening applied on live page" />
                    <div className="absolute inset-x-2 bottom-2 flex items-center justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                      <button type="button" onClick={() => removeBackgroundAt(i)} className="bg-white/90 text-[color:var(--pd-danger)] text-xs px-2 py-1 rounded" data-testid={`admin-background-remove-${i}`}>Remove</button>
                    </div>
                    <div className="absolute top-2 left-2 bg-[color:var(--pd-gold)] text-[#14251c] text-[10px] font-mono uppercase px-2 py-0.5 rounded">Slot {i + 1}</div>
                  </div>
                ))}
                {(form.background_images || []).length > 3 ? (
                  <div className="col-span-full text-xs text-[color:var(--pd-accent-orange)]">
                    Only the first 3 images are used as backgrounds; extras beyond that are ignored on the live page.
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Basic details */}
          <Field label="Title"><input required value={form.title} onChange={(e)=>update('title', e.target.value)} className={input} /></Field>
          <Field label="Slug (URL)" w="half"><input required value={form.slug} onChange={(e)=>update('slug', e.target.value)} className={input} /></Field>
          <Field label="Category" w="half">
            <select value={form.category} onChange={(e)=>update('category', e.target.value)} className={input}>
              {['Padel Holidays','Premium Rackets','Club Memberships','Bundles','HYROX Packages'].map((c)=>(<option key={c}>{c}</option>))}
            </select>
          </Field>
          <Field label="Subtitle"><input value={form.subtitle} onChange={(e)=>update('subtitle', e.target.value)} className={input} /></Field>
          <Field label="Ticket price (£)" w="half"><input type="number" step="0.01" required value={form.ticket_price_gbp} onChange={(e)=>update('ticket_price_gbp', e.target.value)} className={input} /></Field>
          <Field label="Cash alternative (£)" w="half"><input type="number" step="0.01" value={form.cash_alternative_gbp} onChange={(e)=>update('cash_alternative_gbp', e.target.value)} className={input} /></Field>
          <Field label="Total tickets" w="half"><input type="number" required value={form.total_tickets} onChange={(e)=>update('total_tickets', e.target.value)} className={input} /></Field>
          <Field label="Free entry cap (blank = same as total tickets)" w="half"><input type="number" min={0} value={form.free_entry_cap ?? ''} onChange={(e)=>update('free_entry_cap', e.target.value)} placeholder="e.g. 1000" className={input} data-testid="admin-comp-free-cap" /></Field>
          <Field label="Draw date (ISO e.g. 2026-08-01T18:00:00Z)"><input required value={form.draw_date} onChange={(e)=>update('draw_date', e.target.value)} className={input} /></Field>
          <Field label="Location"><input value={form.location || ''} onChange={(e)=>update('location', e.target.value)} className={input} /></Field>

          <Field label="Prize description"><textarea rows={4} required value={form.prize_description} onChange={(e)=>update('prize_description', e.target.value)} className={textarea} /></Field>
          <Field label="Details bullets (one per line)"><textarea rows={4} value={form.details_bullets} onChange={(e)=>update('details_bullets', e.target.value)} className={textarea} /></Field>
          <Field label="Skill question"><input required value={form.skill_question} onChange={(e)=>update('skill_question', e.target.value)} className={input} /></Field>
          <Field label="Skill options (one per line, first is default correct)"><textarea rows={4} required value={form.skill_options} onChange={(e)=>update('skill_options', e.target.value)} className={textarea} /></Field>
          <Field label="Correct index (0-based)" w="half"><input type="number" value={form.skill_correct_index} onChange={(e)=>update('skill_correct_index', e.target.value)} className={input} /></Field>
          <Field label="Status" w="half">
            <select value={form.status || 'COMING_SOON'} onChange={(e)=>update('status', e.target.value)} className={input} data-testid="admin-comp-status">
              {['COMING_SOON','LIVE','SOLD_OUT','WINNER_ANNOUNCED','CANCELLED'].map((s)=>(<option key={s} value={s}>{s.replace(/_/g,' ')}</option>))}
            </select>
          </Field>
          {form.status === 'COMING_SOON' ? (
            <Field
              label="Scheduled go-live (optional)"
              w="half"
              hint="Leave blank to keep in Coming Soon until you manually flip to LIVE. When set, the site auto-flips this competition at that exact time."
            >
              <input
                type="datetime-local"
                value={form.go_live_at ? form.go_live_at.slice(0,16) : ''}
                onChange={(e)=>update('go_live_at', e.target.value)}
                className={input}
                data-testid="admin-comp-go-live-at"
              />
            </Field>
          ) : (
            <Field label="Scheduled go-live (optional)" w="half" hint="Only used when status is Coming Soon.">
              <input disabled value="—" className={`${input} opacity-50 cursor-not-allowed`} />
            </Field>
          )}

          <label className="flex items-center gap-2 md:col-span-2 text-sm">
            <input type="checkbox" checked={!!form.featured} onChange={(e)=>update('featured', e.target.checked)} className="accent-[color:var(--pd-accent-yellow)]" />
            Featured on homepage
          </label>
          <label className="flex items-start gap-2 md:col-span-2 text-sm rounded-xl border border-pd bg-[color:var(--pd-bg)] px-3 py-3">
            <input type="checkbox" checked={!!form.instant_win} onChange={(e)=>update('instant_win', e.target.checked)} className="mt-0.5 accent-[color:var(--pd-accent-yellow)]" data-testid="admin-comp-instant-win" />
            <span>
              <span className="text-[color:var(--pd-text)]">Instant-win format</span>
              <span className="block text-xs text-muted-sage mt-1">
                Under DCMS Code clause 1.3, credit cards are not accepted on instant-win draws.
                Any credit-card payment received will be auto-refunded and no tickets will be issued.
              </span>
            </span>
          </label>

          <div className="md:col-span-2 flex gap-3 sticky bottom-3 z-10 bg-[color:var(--pd-bg)]/95 backdrop-blur border border-pd rounded-xl px-3 py-3">
            <button disabled={loading} className="bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-xl px-5 py-3 font-medium disabled:opacity-60" data-testid="admin-comp-save">
              {loading ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => nav('/admin/competitions')} className="border border-pd rounded-xl px-4 py-3">Cancel</button>
            {id ? (
              <button type="button" onClick={() => setPreviewKey((k) => k + 1)} className="border border-pd rounded-xl px-4 py-3 text-sm ml-auto">
                Refresh preview
              </button>
            ) : null}
          </div>
        </form>

        {/* LIVE PREVIEW */}
        <aside className="xl:sticky xl:top-4 xl:self-start">
          <div className="rounded-2xl border border-pd bg-[color:var(--pd-surface)] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-pd">
              <div>
                <div className="font-mono text-[10px] uppercase text-muted-sage">Live preview</div>
                <div className="text-sm">{form.slug ? `/competitions/${form.slug}` : 'Save the draft first to preview'}</div>
              </div>
              {form.slug ? (
                <a href={previewSrc} target="_blank" rel="noreferrer" className="text-[10px] font-mono uppercase text-muted-sage hover:text-[color:var(--pd-text)]">Open ↗</a>
              ) : null}
            </div>
            {form.slug && id ? (
              <iframe
                key={previewKey}
                title="Preview"
                src={previewSrc}
                className="w-full h-[70vh] bg-white"
                sandbox="allow-same-origin allow-scripts allow-forms"
              />
            ) : (
              <div className="h-[70vh] flex flex-col items-center justify-center text-center gap-2 px-6">
                <div className="font-display text-lg">Preview appears after first Save</div>
                <div className="text-xs text-muted-sage max-w-xs">Fill in the essentials (title, slug, hero image) and press Save. This panel will then show your competition page exactly as customers will see it.</div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};
export default AdminCompetitionEditor;
